import React, { useEffect, useRef, useState } from "react";
import { TrendingUp, Shield, Users, Target } from "lucide-react";

const reasons = [
  {
    icon: TrendingUp,
    title: "Institutional-Grade Investment Structuring",
    description:
      "Learn how to structure investments that meet institutional standards and regulatory requirements for long-term capital deployment.",
  },
  {
    icon: Target,
    title: "Capital Market Alignment",
    description:
      "Understand the frameworks and processes that align professional practices with capital market infrastructure and institutional expectations.",
  },
  {
    icon: Shield,
    title: "Compliance and Governance Frameworks",
    description:
      "Master the compliance, governance, and operational standards required for institutional-grade capital management and investor servicing.",
  },
  {
    icon: Users,
    title: "Long-Term Capital Relationship Building",
    description:
      "Develop the skills to build sustainable, trust-based relationships with HNIs, UHNIs, family offices, and institutional investors.",
  },
];

export default function WhyThisMatters() {
  const [activeIndex, setActiveIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement | null>(null);

  /* 🔹 AUTO SLIDE (mobile only) */
  useEffect(() => {
    const interval = setInterval(() => {
      if (!carouselRef.current) return;

      const container = carouselRef.current;
      const cardWidth = container.offsetWidth * 0.85 + 16; // card width + gap
      const nextIndex = (activeIndex + 1) % reasons.length;

      container.scrollTo({
        left: cardWidth * nextIndex,
        behavior: "smooth",
      });

      setActiveIndex(nextIndex);
    }, 3500);

    return () => clearInterval(interval);
  }, [activeIndex]);

  return (
    <section
      className="
        relative
        py-14 sm:py-16 md:py-20
        px-4 sm:px-6 md:px-12 lg:px-24
        bg-gradient-to-b from-[#F8FAFC] via-white to-[#F1F5F9]
        overflow-hidden
      "
    >
      {/* Background accent */}
      <div className="absolute inset-x-0 top-40 h-[320px] md:h-[400px] bg-[#0E6F7E]/5 blur-3xl"></div>

      <div className="container mx-auto max-w-7xl relative z-10">
        {/* Heading */}
        <div className="text-center mb-8 sm:mb-10">
          <div className="inline-block">
            <div className="h-1 w-14 sm:w-16 bg-gradient-to-r from-[#D4AF37] to-[#C9A631] mx-auto mb-5 sm:mb-6"></div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#07162E]">
              Why This Program Matters
            </h2>
          </div>
        </div>

        {/* 🔹 MOBILE AUTO CAROUSEL */}
        <div className="sm:hidden overflow-hidden">
          <div
            ref={carouselRef}
            className="
              flex gap-4 overflow-x-hidden snap-x snap-mandatory
              pb-4 -mx-4 px-4
              scroll-smooth
            "
            onScroll={(e) => {
              const scrollLeft = e.currentTarget.scrollLeft;
              const cardWidth = e.currentTarget.offsetWidth * 0.85 + 16;
              setActiveIndex(Math.round(scrollLeft / cardWidth));
            }}
          >
            {reasons.map((reason, index) => (
              <div
                key={index}
                className="
                  snap-center shrink-0
                  w-[85%]
                  bg-white/80 backdrop-blur-xl
                  rounded-2xl
                  p-5
                  shadow-[0_20px_50px_rgba(2,8,23,0.08)]
                  border-l-4 border-[#0E6F7E]
                "
              >
                <div className="flex items-start gap-4">
                  <div
                    className="
                      w-12 h-12 rounded-xl
                      bg-gradient-to-br from-[#07162E] via-[#0B2A4A] to-[#0E6F7E]
                      flex items-center justify-center
                    "
                  >
                    <reason.icon className="w-6 h-6 text-[#1DBF73]" />
                  </div>

                  <div>
                    <h3 className="text-base font-bold text-[#07162E] mb-2">
                      {reason.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {reason.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-4">
            {reasons.map((_, idx) => (
              <button
                key={idx}
                onClick={() => {
                  if (!carouselRef.current) return;
                  const cardWidth = carouselRef.current.offsetWidth * 0.85 + 16;
                  carouselRef.current.scrollTo({
                    left: cardWidth * idx,
                    behavior: "smooth",
                  });
                  setActiveIndex(idx);
                }}
                className={`h-2 w-2 rounded-full transition-all ${
                  idx === activeIndex
                    ? "bg-[#0E6F7E] scale-110"
                    : "bg-gray-300"
                }`}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        </div>

        {/* 🔹 DESKTOP / TABLET GRID */}
        <div className="hidden sm:grid sm:grid-cols-2 gap-6 md:gap-8 max-w-6xl mx-auto">
          {reasons.map((reason, index) => (
            <div
              key={index}
              className="
                group bg-white/80 backdrop-blur-xl
                rounded-2xl p-6 md:p-8
                shadow-[0_20px_50px_rgba(2,8,23,0.08)]
                hover:shadow-[0_30px_70px_rgba(2,8,23,0.15)]
                transition-all duration-300
                hover:-translate-y-1 md:hover:-translate-y-2
                border-l-4 border-[#0E6F7E]
              "
            >
              <div className="flex items-start gap-4">
                <div
                  className="
                    w-14 h-14 rounded-xl
                    bg-gradient-to-br from-[#07162E] via-[#0B2A4A] to-[#0E6F7E]
                    flex items-center justify-center
                    group-hover:scale-110 transition-transform duration-300
                  "
                >
                  <reason.icon className="w-7 h-7 text-[#1DBF73]" />
                </div>

                <div>
                  <h3 className="text-lg md:text-xl font-bold text-[#07162E] mb-3">
                    {reason.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {reason.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}